import React, { useEffect, useRef, useState } from "react";
import { View, ScrollView } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { Building2, GitMerge, Hash } from "lucide-react-native";
import {
  useFertilityTreatments,
  type FETProtocolClass,
  type FETResultState,
  type FETThawState,
  type FETTransferState,
} from "../FertilityTreatmentsContext";
import { getNextTreatmentScreen } from "../getNextTreatmentScreen";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import {
  CounterInput,
  FertilityTreatmentCycleCard,
  MedicalHistoryTextField,
  QuestionLayout,
} from "@src/packages/medical-history/components";
import { DateField, MoonSelectField } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import type { FertilityTreatmentsStackParamList } from "../FertilityTreatmentsNavigator";
import { hasHistoricalTreatmentDetails } from "../transformFertilityTreatmentsState";
import { useFETHistoricalRecords } from "../useFETHistoricalRecords";
import { createIdempotencyKey } from "@src/packages/care/cryovault/useSpecimens";

type Cycle = {
  id?: string;
  historyRecordId?: string;
  historyClientRequestId?: string;
  date: string;
  clinic: string;
  outcome: string | null;
  protocolClass: FETProtocolClass;
  transferState: FETTransferState;
  thawState: FETThawState;
  resultState: FETResultState;
  embryoCount: number | null;
};

function emptyCycle(): Cycle {
  return {
    date: "",
    clinic: "",
    outcome: null,
    protocolClass: "unknown",
    transferState: "unknown",
    thawState: "unknown",
    resultState: "unknown",
    embryoCount: null,
  };
}

function resultStateToOutcome(
  resultState: string | null | undefined,
): string | null {
  switch (resultState) {
    case "negative":
      return "no_pregnancy";
    case "pregnancy_detected":
    case "pregnancy_unknown_location":
    case "ongoing":
      return "pregnancy";
    case "biochemical_pregnancy":
      return "chemical_pregnancy";
    case "ectopic_pregnancy":
      return "ectopic_pregnancy";
    case "early_pregnancy_loss":
      return "miscarriage";
    case "stillbirth":
      return "still_birth";
    case "live_birth":
      return "live_birth";
    case "no_transfer":
    case "cancelled":
      return "cancelled";
    default:
      return null;
  }
}

export function FETTreatmentsScreen() {
  const t = useTranslations();
  const { state, updateState } = useFertilityTreatments();
  const { data: historicalRecords } = useFETHistoricalRecords();
  const navigation =
    useNavigation<NavigationProp<FertilityTreatmentsStackParamList>>();

  const [count, setCount] = useState<number>(() => {
    const existing = (state.treatments || []).filter(
      (treatment) => treatment.type === "fet",
    );
    return existing.length > 0 ? existing.length : 1;
  });

  const [cycles, setCycles] = useState<Cycle[]>(() => {
    const existing = (state.treatments || []).filter(
      (treatment) => treatment.type === "fet",
    );
    if (existing.length === 0) return [emptyCycle()];
    return existing.map((treatment) => ({
      id: treatment.id,
      historyRecordId: treatment.historyRecordId,
      historyClientRequestId: treatment.historyClientRequestId,
      date: treatment.date ?? "",
      clinic: treatment.clinic ?? "",
      outcome: treatment.outcome ?? null,
      protocolClass: treatment.protocolClass ?? "unknown",
      transferState: treatment.transferState ?? "unknown",
      thawState: treatment.thawState ?? "unknown",
      resultState: treatment.resultState ?? "unknown",
      embryoCount: treatment.embryoCount ?? null,
    }));
  });

  const hasHydratedHistoricalRecords = useRef(false);
  useEffect(() => {
    if (
      historicalRecords === undefined ||
      hasHydratedHistoricalRecords.current
    ) {
      return;
    }

    hasHydratedHistoricalRecords.current = true;
    setCycles((previous) =>
      previous.map((cycle) => {
        const record = historicalRecords.find(
          (candidate) =>
            candidate.cycleId === cycle.id &&
            candidate.recordStatus === "current",
        );
        if (!record) return cycle;

        return {
          ...cycle,
          historyRecordId: record.id,
          date: record.startedAt ?? cycle.date,
          clinic: record.clinicName ?? cycle.clinic,
          outcome: resultStateToOutcome(record.resultState) ?? cycle.outcome,
          protocolClass: record.protocolClass,
          transferState: record.transferState,
          thawState: record.thawState,
          resultState: record.resultState,
          embryoCount: record.embryoCount,
        };
      }),
    );
  }, [historicalRecords]);

  const ensureLength = (nextCount: number) => {
    setCycles((prev) => {
      const arr = [...prev];
      while (arr.length < nextCount) {
        arr.push(emptyCycle());
      }
      if (arr.length > nextCount) arr.length = nextCount;
      return arr;
    });
  };

  const onNext = () => {
    const otherTreatments = state.treatments.filter(
      (treatment) => treatment.type !== "fet",
    );
    const nextTreatments = [
      ...otherTreatments,
      ...cycles.map((c) => ({
        ...(c.id ? { id: c.id } : {}),
        ...(c.historyRecordId ? { historyRecordId: c.historyRecordId } : {}),
        historyClientRequestId:
          c.historyClientRequestId ?? createIdempotencyKey(),
        type: "fet",
        name: null,
        date: c.date || null,
        clinic: c.clinic || null,
        outcome: c.outcome || null,
        protocolClass: c.protocolClass,
        transferState: c.transferState,
        thawState: c.thawState,
        resultState: c.resultState,
        embryoCount: c.embryoCount,
      })),
    ];
    updateState({
      treatments: nextTreatments,
      currentNavigationIndex: (state.currentNavigationIndex ?? 0) + 1,
    });
    const next = getNextTreatmentScreen({
      ...state,
      treatments: nextTreatments,
      currentNavigationIndex: (state.currentNavigationIndex ?? 0) + 1,
    });
    navigation.navigate(next as keyof FertilityTreatmentsStackParamList);
  };

  return (
    <QuestionLayout
      title={t("med_history.fertility.treatments.fet.title")}
      subtitle={t("med_history.fertility.treatments.common.details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={onNext}
      saveLabel={t("med_history.fertility.continue")}
    >
      <ScrollView contentContainerStyle={{ gap: 16, paddingBottom: 24 }}>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 13, lineHeight: 19, color: "#6f625a" }}
        >
          {t("med_history.fertility.treatments.fet.patient_report_notice")}
        </Text>
        <CounterInput
          testID="medical-history-treatment-cycle-counter"
          label={t("med_history.fertility.treatments.fet.counter_label")}
          description={t(
            "med_history.fertility.treatments.fet.counter_description",
          )}
          value={count}
          min={1}
          onIncrement={() => {
            const nextCount = count + 1;
            setCount(nextCount);
            ensureLength(nextCount);
          }}
          onDecrement={() => {
            const nextCount = Math.max(1, count - 1);
            setCount(nextCount);
            ensureLength(nextCount);
          }}
          decrementConfirmation={{
            shouldConfirm: () =>
              hasHistoricalTreatmentDetails(cycles[count - 1] ?? {}),
            title: t(
              "med_history.fertility.treatments.common.remove_cycle_title",
            ),
            message: t(
              "med_history.fertility.treatments.common.remove_cycle_message",
            ),
            cancelLabel: t(
              "med_history.fertility.treatments.common.remove_cycle_cancel",
            ),
            confirmLabel: t(
              "med_history.fertility.treatments.common.remove_cycle_confirm",
            ),
          }}
        />

        {cycles.map((c, i) => (
          <FertilityTreatmentCycleCard
            key={i}
            testID={`medical-history-treatment-cycle-${i + 1}`}
            cycleLabel={t("med_history.fertility.treatments.common.cycle", {
              params: { n: String(i + 1) },
            })}
          >
            <View>
              <DateField
                label={t("med_history.fertility.treatments.common.date_label")}
                value={c.date}
                onChange={(iso) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = { ...arr[i]!, date: iso };
                    return arr;
                  })
                }
              />
            </View>
            <View>
              <MedicalHistoryTextField
                label={t(
                  "med_history.fertility.treatments.common.clinic_label",
                )}
                placeholder={t(
                  "med_history.fertility.treatments.common.clinic_placeholder",
                )}
                icon={Building2}
                value={c.clinic || ""}
                onChangeText={(text) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = { ...arr[i]!, clinic: text };
                    return arr;
                  })
                }
                style={{ width: "100%" }}
              />
            </View>
            <View>
              <MoonSelectField
                label={t("med_history.fertility.treatments.fet.protocol_label")}
                placeholder={t(
                  "med_history.fertility.treatments.fet.unknown_placeholder",
                )}
                value={c.protocolClass}
                onChange={(value: string | null) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = {
                      ...arr[i]!,
                      protocolClass: (value ?? "unknown") as FETProtocolClass,
                    };
                    return arr;
                  })
                }
                labelIcon={GitMerge}
                options={(
                  [
                    ["unknown", "unknown"],
                    ["true_natural", "true_natural"],
                    ["modified_natural", "modified_natural"],
                    ["programmed", "programmed"],
                    ["stimulated", "stimulated"],
                    ["down_regulated", "down_regulated"],
                    ["other", "other"],
                  ] as const
                ).map(([value, key]) => ({
                  value,
                  label: t(
                    `med_history.fertility.treatments.fet.protocol_${key}`,
                  ),
                }))}
              />
            </View>
            <View>
              <MoonSelectField
                label={t(
                  "med_history.fertility.treatments.fet.transfer_state_label",
                )}
                placeholder={t(
                  "med_history.fertility.treatments.fet.unknown_placeholder",
                )}
                value={c.transferState}
                onChange={(value: string | null) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = {
                      ...arr[i]!,
                      transferState: (value ?? "unknown") as FETTransferState,
                    };
                    return arr;
                  })
                }
                labelIcon={GitMerge}
                options={[
                  "unknown",
                  "planned",
                  "scheduled",
                  "performed",
                  "cancelled",
                  "no_transfer",
                  "thaw_failure",
                  "paused",
                ].map((value) => ({
                  value,
                  label: t(
                    `med_history.fertility.treatments.fet.transfer_${value}`,
                  ),
                }))}
              />
            </View>
            <View>
              <MoonSelectField
                label={t(
                  "med_history.fertility.treatments.fet.thaw_state_label",
                )}
                placeholder={t(
                  "med_history.fertility.treatments.fet.unknown_placeholder",
                )}
                value={c.thawState}
                onChange={(value: string | null) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = {
                      ...arr[i]!,
                      thawState: (value ?? "unknown") as FETThawState,
                    };
                    return arr;
                  })
                }
                labelIcon={GitMerge}
                options={[
                  "unknown",
                  "not_applicable",
                  "ordered",
                  "survived",
                  "failed",
                  "partial",
                  "not_reported",
                ].map((value) => ({
                  value,
                  label: t(
                    `med_history.fertility.treatments.fet.thaw_${value}`,
                  ),
                }))}
              />
            </View>
            <View>
              <MedicalHistoryTextField
                label={t(
                  "med_history.fertility.treatments.fet.embryo_count_label",
                )}
                placeholder={t(
                  "med_history.fertility.treatments.fet.embryo_count_placeholder",
                )}
                icon={Hash}
                value={c.embryoCount === null ? "" : String(c.embryoCount)}
                onChangeText={(text) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    const normalized = text.trim();
                    const parsed = Number.parseInt(normalized, 10);
                    arr[i] = {
                      ...arr[i]!,
                      embryoCount:
                        normalized === "" ||
                        !Number.isSafeInteger(parsed) ||
                        parsed < 0
                          ? null
                          : parsed,
                    };
                    return arr;
                  })
                }
                keyboardType="numeric"
                style={{ width: "100%" }}
              />
            </View>
            <View>
              <MoonSelectField
                label={t(
                  "med_history.fertility.treatments.fet.result_state_label",
                )}
                placeholder={t(
                  "med_history.fertility.treatments.fet.unknown_placeholder",
                )}
                value={c.resultState}
                onChange={(v: string | null) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    const nextResultState = (v ?? "unknown") as FETResultState;
                    arr[i] = {
                      ...arr[i]!,
                      resultState: nextResultState,
                      outcome: resultStateToOutcome(nextResultState),
                    };
                    return arr;
                  })
                }
                labelIcon={GitMerge}
                options={[
                  "unknown",
                  "not_yet_known",
                  "negative",
                  "indeterminate_repeat",
                  "pregnancy_detected",
                  "pregnancy_unknown_location",
                  "ongoing",
                  "biochemical_pregnancy",
                  "ectopic_pregnancy",
                  "early_pregnancy_loss",
                  "stillbirth",
                  "live_birth",
                  "no_transfer",
                  "cancelled",
                  "paused",
                ].map((value) => ({
                  value,
                  label: t(
                    `med_history.fertility.treatments.fet.result_${value}`,
                  ),
                }))}
              />
            </View>
          </FertilityTreatmentCycleCard>
        ))}
      </ScrollView>
    </QuestionLayout>
  );
}

export default FETTreatmentsScreen;
